import React, { useState } from 'react';
import axios from 'axios';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card, CardContent } from './ui/card';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/contacts`, formData);
      
      // Track conversion in Google Analytics 4
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'generate_lead', {
          event_category: 'Contact',
          event_label: 'Contact Form Submission',
          value: 1
        });
      }
      
      toast.success('Message envoyé avec succès!', {
        description: 'Nous vous contacterons dans les plus brefs délais.',
      });
      
      // Reset form
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast.error('Erreur lors de l\'envoi', {
        description: 'Veuillez réessayer plus tard.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="contact" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-[#1e5a7a] mb-4">
            Contactez-nous
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Notre équipe d'experts est à votre écoute pour réaliser vos projets immobiliers.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <div className="space-y-8">
            <div>
              <h3 className="text-2xl font-bold text-[#1e5a7a] mb-6">
                Nos Coordonnées
              </h3>
              <div className="space-y-6">
                <Card className="border-l-4 border-l-[#f5b800] shadow-md hover:shadow-lg transition-shadow">
                  <CardContent className="p-6 flex items-start">
                    <div className="flex-shrink-0 w-12 h-12 bg-[#1e5a7a] rounded-full flex items-center justify-center">
                      <MapPin className="text-white" size={24} />
                    </div>
                    <div className="ml-4">
                      <h4 className="font-semibold text-[#1e5a7a] mb-1">Adresse</h4>
                      <p className="text-gray-700">Route de berne 223</p>
                      <p className="text-gray-700">1066 Epalinges, Suisse</p>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-l-[#f5b800] shadow-md hover:shadow-lg transition-shadow">
                  <CardContent className="p-6 flex items-start">
                    <div className="flex-shrink-0 w-12 h-12 bg-[#1e5a7a] rounded-full flex items-center justify-center">
                      <Phone className="text-white" size={24} />
                    </div>
                    <div className="ml-4">
                      <h4 className="font-semibold text-[#1e5a7a] mb-1">Téléphone</h4>
                      <p className="text-gray-700">+41 (0)79 510 08 55</p>
                      <p className="text-sm text-gray-500">Lun-Ven: 9h00 - 17h00</p>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-l-[#f5b800] shadow-md hover:shadow-lg transition-shadow">
                  <CardContent className="p-6 flex items-start">
                    <div className="flex-shrink-0 w-12 h-12 bg-[#1e5a7a] rounded-full flex items-center justify-center">
                      <Mail className="text-white" size={24} />
                    </div>
                    <div className="ml-4">
                      <h4 className="font-semibold text-[#1e5a7a] mb-1">Email</h4>
                      <p className="text-gray-700">info@immoconceptsuisse.com</p>
                      <p className="text-gray-700">guillaume@immoconceptsuisse.com</p>
                      <p className="text-sm text-gray-500">Réponse sous 24h</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            <div className="bg-gradient-to-br from-[#1e5a7a] to-[#15475c] rounded-2xl p-8 text-white">
              <h3 className="text-2xl font-bold mb-4">Horaires d'ouverture</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span>Lundi - Vendredi</span>
                  <span className="font-semibold">9h00 - 18h00</span>
                </div>
                <div className="flex justify-between">
                  <span>Samedi</span>
                  <span className="font-semibold">Fermé (sur RDV)</span>
                </div>
                <div className="flex justify-between">
                  <span>Dimanche</span>
                  <span className="font-semibold">Fermé</span>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <Card className="shadow-xl border-0">
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-[#1e5a7a] mb-2">
                    Nom complet *
                  </label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Votre nom"
                    className="border-gray-300 focus:border-[#1e5a7a] focus:ring-[#1e5a7a]"
                    disabled={isSubmitting}
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-[#1e5a7a] mb-2">
                    Email *
                  </label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="votre@email.fr"
                    className="border-gray-300 focus:border-[#1e5a7a] focus:ring-[#1e5a7a]"
                    disabled={isSubmitting}
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-[#1e5a7a] mb-2">
                    Téléphone
                  </label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+33 6 00 00 00 00"
                    className="border-gray-300 focus:border-[#1e5a7a] focus:ring-[#1e5a7a]"
                    disabled={isSubmitting}
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-[#1e5a7a] mb-2">
                    Message *
                  </label>
                  <Textarea
                    id="message"
                    name="message"
                    required
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Décrivez votre projet..."
                    rows={5}
                    className="border-gray-300 focus:border-[#1e5a7a] focus:ring-[#1e5a7a]"
                    disabled={isSubmitting}
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#f5b800] hover:bg-[#d9a400] text-black font-semibold py-6 text-lg transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Send className="mr-2" size={20} />
                  {isSubmitting ? 'Envoi en cours...' : 'Envoyer le message'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};